import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { Modal, Row, Col, Table, Card, Form, Button } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { FaSearch, FaPrint, FaStore, FaChartLine, FaFilter, FaTimes, FaChevronDown, FaChevronUp, FaPhone, FaBox } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from 'react-select';

export default function VendorReport() {
    const { t } = useContext(TranslatorContext);
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState('');
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [showFilters, setShowFilters] = useState(true);
    const [expandedVendors, setExpandedVendors] = useState({});
    const [selectedVendorOption, setSelectedVendorOption] = useState(null);

    const vendorOptions = vendors.map(vendor => ({
        value: vendor.id,
        label: `${vendor.name} (${vendor.phone})`
    }));

    const [summary, setSummary] = useState({
        totalPurchase: 0,
        totalSale: 0,
        totalProfit: 0
    });

    useEffect(() => {
        fetchVendors();
    }, []);

    useEffect(() => {
        if (startDate && endDate) {
            fetchReport();
        }
    }, [startDate, endDate]);

    const fetchVendors = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/vendor-report`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
            });
            setVendors(res.data.vendors || []);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchReport = async () => {
        setLoading(true);
        try {
            const params = {};
            if (selectedVendor) params.vendor_id = selectedVendor;
            if (startDate) params.start_date = startDate.toISOString().split('T')[0];
            if (endDate) params.end_date = endDate.toISOString().split('T')[0];

            const res = await axios.get(`${process.env.REACT_APP_API_URL}/vendor-report`, {
                params: params,
                headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
            });

            setReport(res.data.report || []);

            const totals = (res.data.report || []).reduce((acc, vendor) => ({
                totalPurchase: acc.totalPurchase + (vendor.summary?.total_purchase || 0),
                totalSale: acc.totalSale + (vendor.summary?.total_sale || 0),
                totalProfit: acc.totalProfit + (vendor.summary?.total_profit || 0)
            }), { totalPurchase: 0, totalSale: 0, totalProfit: 0 });

            setSummary(totals);

            const expanded = {};
            (res.data.report || []).forEach(vendor => {
                expanded[vendor.vendor_id] = true;
            });
            setExpandedVendors(expanded);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleFilter = () => {
        fetchReport();
    };

    const handleReset = () => {
        setSelectedVendor('');
        setSelectedVendorOption(null);
        setStartDate(new Date());
        setEndDate(new Date());
        setTimeout(() => fetchReport(), 100);
    };

    const toggleVendor = (vendorId) => {
        setExpandedVendors(prev => ({
            ...prev,
            [vendorId]: !prev[vendorId]
        }));
    };

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null) return '0';

        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const exportToPDF = () => {
        let url = `${process.env.REACT_APP_URL}/report/vendor-report`;
        const params = new URLSearchParams();
        if (selectedVendor) params.append("vendor_id", selectedVendor);
        if (startDate) params.append("start_date", startDate.toISOString().split('T')[0]);
        if (endDate) params.append("end_date", endDate.toISOString().split('T')[0]);
        const finalUrl = `${url}?${params.toString()}`;
        window.open(finalUrl, "_blank");
    };

    return (
        <PageLayout>
            <div className="mb-4">
                <h2 className="mb-1 fw-bold text-dark">
                    <FaChartLine className="me-2 text-primary" />
                    {t('Vendor Report')}
                </h2>
                <p className="text-muted mb-0 small">{t('View vendor purchase and sales performance')}</p>
            </div>

            {/* Filter Section - Single Row */}
            <Card className="shadow-sm mb-4 border-0">
                <Card.Body className="p-3 p-md-4">
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <h5 className="mb-0 d-flex align-items-center gap-2 fs-6 fw-bold text-primary">
                            <FaFilter size={14} />
                            {t('Filters')}
                        </h5>
                        <Button
                            variant="link"
                            onClick={() => setShowFilters(!showFilters)}
                            className="text-decoration-none p-0"
                            size="sm"
                        >
                            {showFilters ? <FaTimes /> : <FaFilter />}
                        </Button>
                    </div>

                    {showFilters && (
                        <Row className="g-3 align-items-end">
                            <Col xs={12} md={3}>
                                <Form.Label className="fw-semibold small mb-1">{t('Vendor')}</Form.Label>
                                <Select
                                    options={vendorOptions}
                                    value={selectedVendorOption}
                                    onChange={(option) => {
                                        setSelectedVendorOption(option);
                                        setSelectedVendor(option ? option.value : '');
                                    }}
                                    isClearable
                                    placeholder={t('Search vendor...')}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </Col>
                            <Col xs={12} md={2}>
                                <Form.Label className="fw-semibold small mb-1">{t('Start Date')}</Form.Label>
                                <DatePicker
                                    selected={startDate}
                                    onChange={setStartDate}
                                    className="form-control form-control-sm rounded-3"
                                    placeholderText="Select start date"
                                    dateFormat="yyyy-MM-dd"
                                    isClearable
                                />
                            </Col>
                            <Col xs={12} md={2}>
                                <Form.Label className="fw-semibold small mb-1">{t('End Date')}</Form.Label>
                                <DatePicker
                                    selected={endDate}
                                    onChange={setEndDate}
                                    className="form-control form-control-sm rounded-3"
                                    placeholderText="Select end date"
                                    dateFormat="yyyy-MM-dd"
                                    isClearable
                                />
                            </Col>
                            <Col xs={12} md={2}>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="primary"
                                        onClick={handleFilter}
                                        className="flex-grow-1 d-flex align-items-center justify-content-center gap-2"
                                        size="sm"
                                    >
                                        <FaSearch size={12} />
                                        {t('Search')}
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={handleReset}
                                        size="sm"
                                    >
                                        {t('Reset')}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            {/* Action Buttons */}
            <div className="mb-3 d-flex justify-content-end no-print">
                <Button variant="danger" onClick={exportToPDF} className="rounded-3 d-flex align-items-center gap-2" size="sm">
                    <FaPrint size={14} />
                    {t('Export to PDF')}
                </Button>
            </div>

            {/* Vendor-wise Report Tables */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : report.length === 0 ? (
                <Card className="shadow-sm border-0">
                    <Card.Body className="text-center py-5">
                        <div className="mb-3 fs-1 text-muted">📊</div>
                        <p className="text-muted mb-0">{t('No data found')}</p>
                    </Card.Body>
                </Card>
            ) : (
                report.map((vendor) => (
                    <Card key={vendor.vendor_id} className="shadow-sm mb-4 border-0 vendor-card">
                        {/* Vendor Header */}
                        <div
                            className="vendor-header p-3 p-md-4 d-flex justify-content-between align-items-center flex-wrap gap-3"
                            onClick={() => toggleVendor(vendor.vendor_id)}
                            style={{ cursor: 'pointer', backgroundColor: '#2b6cb0' }}
                        >
                            <div className="flex-grow-1">
                                <h5 className="mb-1 fw-bold text-white">
                                    {vendor.vendor_name}
                                </h5>
                                <div className="d-flex flex-wrap gap-3 mt-2">
                                    <small className="text-white-50 d-flex align-items-center gap-1">
                                        <FaPhone size={10} /> {t('Phone')}: {vendor.vendor_phone || '-'}
                                    </small>
                                    <small className="text-white-50 d-flex align-items-center gap-1">
                                        <FaBox size={10} /> {t('Products')}: {vendor.products.length}
                                    </small>
                                </div>
                            </div>
                            <div className="d-flex gap-3 align-items-center flex-wrap">
                                <div className="text-end">
                                    <div className="text-white-50 small">{t('Total Purchase')}</div>
                                    <strong className="text-white">{formatCurrency(vendor.summary.total_purchase)}</strong>
                                </div>
                                <div className="text-end">
                                    <div className="text-white-50 small">{t('Total Sale')}</div>
                                    <strong className="text-white">{formatCurrency(vendor.summary.total_sale)}</strong>
                                </div>
                                <div className="text-end">
                                    <div className="text-white-50 small">{t('Total Profit')}</div>
                                    <strong className={vendor.summary.total_profit >= 0 ? 'text-warning' : 'text-danger'}>
                                        {formatCurrency(vendor.summary.total_profit)}
                                    </strong>
                                </div>
                                <div className="text-white">
                                    {expandedVendors[vendor.vendor_id] ? <FaChevronUp size={18} /> : <FaChevronDown size={18} />}
                                </div>
                            </div>
                        </div>

                        {/* Vendor Products Table */}
                        {expandedVendors[vendor.vendor_id] && (
                            <div className="table-responsive">
                                <Table hover className="mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="p-2 p-md-3 text-center" style={{ width: '5%' }}>#</th>
                                            <th className="p-2 p-md-3">{t('Product Name')}</th>
                                            <th className="p-2 p-md-3 text-end">{t('Purchase Amount')}</th>
                                            <th className="p-2 p-md-3 text-end">{t('Sale Amount')}</th>
                                            <th className="p-2 p-md-3 text-end">{t('Profit')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vendor.products.map((product, idx) => (
                                            <tr key={product.product_id || idx}>
                                                <td className="p-2 p-md-3 text-center">{idx + 1}</td>
                                                <td className="p-2 p-md-3 fw-semibold text-dark">
                                                    {product.product_name}
                                                </td>
                                                <td className="p-2 p-md-3 text-end text-primary fw-semibold">
                                                    {formatCurrency(product.purchase_amount)}
                                                </td>
                                                <td className="p-2 p-md-3 text-end text-success fw-semibold">
                                                    {formatCurrency(product.sale_amount)}
                                                </td>
                                                <td className="p-2 p-md-3 text-end">
                                                    <span className={`badge ${product.profit >= 0 ? 'bg-success' : 'bg-danger'} px-2 py-1`}>
                                                        {formatCurrency(product.profit)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="table-active">
                                        <tr>
                                            <td colSpan="2" className="p-2 p-md-3 fw-bold">{t('Summary')}</td>
                                            <td className="p-2 p-md-3 text-end fw-bold text-primary">
                                                {formatCurrency(vendor.summary.total_purchase)}
                                            </td>
                                            <td className="p-2 p-md-3 text-end fw-bold text-success">
                                                {formatCurrency(vendor.summary.total_sale)}
                                            </td>
                                            <td className="p-2 p-md-3 text-end fw-bold">
                                                <span className={`badge ${vendor.summary.total_profit >= 0 ? 'bg-success' : 'bg-danger'} px-2 py-1`}>
                                                    {formatCurrency(vendor.summary.total_profit)}
                                                </span>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>
                        )}
                    </Card>
                ))
            )}


        </PageLayout>
    );
}